import { Button } from './Button';
import { PS, T } from '../lib/theme';
import { nextFreeLine, occupancyLine } from '../lib/occupancy';
import { formatClock } from '../lib/time';
import type { SlotConflictDetail } from '../lib/types';

interface Props {
  detail: SlotConflictDetail;
  /**
   * Apply `detail.next_free_time` to the surface's own form, if it can.
   *
   * Opt-in per surface, and the *caller's* judgement: only it knows whether the
   * suggestion yields a window worth offering (see `windowAtNextFreeTime` in
   * lib/slot-suggestion.ts, which returns null when it does not). Omit the
   * handler and the banner stays exactly as read-only as it was before admin#50.
   */
  onUseNextFreeTime?: () => void;
}

/**
 * The canonical 409 slot-conflict body (givip/prospin-api#81), rendered.
 *
 * Says what holds the slot and when the table frees up. It does not resolve
 * conflicts — cancelling the occupying booking stays in the bookings grid.
 * `SeriesConflictsBanner` does offer a cancel action, but that banner covers a
 * *projection* over dozens of dates where resolving in place is the only
 * workable flow — a single window has one alternative, so the only action here
 * is moving to it. See .ai/features/47-one-time-coach-booking/DECISIONS.md.
 *
 * Lives in `components/` rather than inside one page so every surface that
 * meets this payload renders it the same way (`NewBookingPage` and
 * `NewOneTimeCoachSessionPage` are the two callers).
 */
export function SlotConflictBanner({ detail, onUseNextFreeTime }: Props) {
  const conflicts = detail.conflicts ?? [];
  if (conflicts.length === 0) return null;

  // Shared with SeriesConflictsBanner so the absent/null/value rule is decided
  // once. This banner covers a single date, so it names none.
  const nextFree = nextFreeLine(detail.next_free_time);

  return (
    <div
      // The panel appears after a submit that looked like it would succeed, and
      // (on the taller booking form) can land below the fold — announce it.
      role="alert"
      style={{
        ...T.caption,
        padding: 12,
        background: PS.errorBg,
        border: `1px solid ${PS.errorBorder}`,
        borderRadius: 3,
        color: PS.errorText,
      }}
    >
      {/* The API authors this sentence per call site — don't second-guess it. */}
      <div style={{ ...T.subheading, color: PS.errorText, marginBottom: 8 }}>{detail.error}</div>

      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {conflicts.map((c, i) => (
          <li key={`${c.date}-${c.start_time}-${c.booking_id ?? i}`} style={{ marginBottom: 4 }}>
            {occupancyLine(c)}
          </li>
        ))}
      </ul>

      {nextFree && (
        <div
          style={{
            ...T.caption,
            color: PS.errorText,
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {/* Kept as a sentence even when the button is present: the button's
              label is a bare time, which says nothing on its own. */}
          <span>{nextFree}</span>
          {detail.next_free_time && onUseNextFreeTime && (
            <Button
              type="button"
              variant="secondary"
              onClick={onUseNextFreeTime}
              style={{ padding: '4px 10px', minHeight: 0 }}
            >
              Use {formatClock(detail.next_free_time)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
